import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  Pressable,
  StyleSheet,
  ScrollView,
  TextInput,
  Platform,
  Alert,
  Modal,
  Dimensions,
  Animated,
  FlatList,
} from 'react-native';
import { StatusBar } from 'expo-status-bar';
import { Image } from 'expo-image';
import { useTheme } from '../context/ThemeContext';
import HamburgerMenu from '../components/HamburgerMenu';
import ProfileIcon from '../components/ProfileIcon';
import Stopwatch from '../components/Stopwatch';
import { getApiUrl } from '../utils/apiConfig';
import {
  exercisesListUrl,
  exerciseTypesByCourseUrl,
} from '../utils/exerciseApi';
import { dedupeCoursesForDropdown } from '../utils/coursesCatalog';
import { useUserPreferences } from '../context/UserPreferencesContext';
import {
  formatDateObjectForInput,
  parseFlexibleDateInput,
  isUSUserFlag,
} from '../utils/dateFormat';

const { width } = Dimensions.get('window');

// Local assets
const COURSES_IMAGE = require('../../assets/ChatGPT Image Jan 1, 2026, 02_28_17 AM.png');
const EXERCISES_IMAGE = require('../../assets/ChatGPT Image Jan 20, 2026, 08_11_59 AM.png');
const GOALS_IMAGE = require('../../assets/ChatGPT Image Mar 13, 2026, 08_37_09 PM.png');

const STATUS_OPTIONS = ['not started', 'in progress', 'completed', 'paused', 'abandoned'];

const capitalizeFirst = (str) => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};
const GOAL_CATEGORY_OPTIONS = ['Strength', 'Balance', 'Flexibility', 'Skill', 'Conditioning', 'Movement'];
const GOAL_TYPE_OPTIONS = ['Sets/Reps', 'Weight', 'Hold Position - Height', 'Distance', 'Skill'];
const TARGET_UNIT_OPTIONS = ['seconds', 'reps', 'sets', 'kg', 'cm', 'meters', 'skill', 'Custom'];

export default function GoalsPage({ navigation, route }) {
  const theme = useTheme();
  const { isUSUser, setIsUSUserFromUser } = useUserPreferences();
  const [currentUser, setCurrentUser] = useState(null);
  const stopwatchRef = useRef(null);
  const scrollViewRef = useRef(null);
  const milestoneSectionRef = useRef(null);
  const [milestoneSectionOffsetY, setMilestoneSectionOffsetY] = useState(0);
  const [milestoneAnchorOffsets, setMilestoneAnchorOffsets] = useState({});
  const [pendingMilestoneScroll, setPendingMilestoneScroll] = useState(null);
  
  // Form state
  const [goalName, setGoalName] = useState('');
  const [selectedExercise, setSelectedExercise] = useState(null);
  const [status, setStatus] = useState('not started');
  const [goalCategory, setGoalCategory] = useState([]);
  const [goalType, setGoalType] = useState(null);
  const [numberOfMilestones, setNumberOfMilestones] = useState('');
  const [estimatedGoalCompletionDate, setEstimatedGoalCompletionDate] = useState('');
  
  // Exercise selection state
  const [showExerciseSection, setShowExerciseSection] = useState(false);
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [exerciseTypes, setExerciseTypes] = useState([]);
  const [selectedExerciseType, setSelectedExerciseType] = useState(null);
  const [exercises, setExercises] = useState([]);
  const [showCoursesDropdown, setShowCoursesDropdown] = useState(false);
  const [showExercisesMenu, setShowExercisesMenu] = useState(false);
  const [showGoalsTable, setShowGoalsTable] = useState(false);
  const [showGradeExercises, setShowGradeExercises] = useState(false);
  const [showAssignedExercises, setShowAssignedExercises] = useState(false);
  const [gradeExercises, setGradeExercises] = useState([]);
  const [assignedExercises, setAssignedExercises] = useState([]);
  const [goalsList, setGoalsList] = useState([]);
  const [loadingGoals, setLoadingGoals] = useState(false);
  const [expandedGoalRowId, setExpandedGoalRowId] = useState(null);
  const [goalMilestonesByGoal, setGoalMilestonesByGoal] = useState({});
  const [loadingGoalMilestonesId, setLoadingGoalMilestonesId] = useState(null);
  const coursesTileColorAnim = useRef(new Animated.Value(1)).current;
  const exercisesTileColorAnim = useRef(new Animated.Value(1)).current;
  const goalsTileColorAnim = useRef(new Animated.Value(1)).current;
  
  // Milestone state
  const [showMilestoneSection, setShowMilestoneSection] = useState(false);
  const [milestones, setMilestones] = useState([]);
  const [automateMilestoneDates, setAutomateMilestoneDates] = useState(false);
  const [expandedMilestones, setExpandedMilestones] = useState({});
  const [openTargetUnitDropdown, setOpenTargetUnitDropdown] = useState(null);
  
  // Picklist dropdowns
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showGoalCategoryDropdown, setShowGoalCategoryDropdown] = useState(false);
  const [showGoalTypeDropdown, setShowGoalTypeDropdown] = useState(false);
  
  // Modal state
  const [showRefreshModal, setShowRefreshModal] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saving, setSaving] = useState(false);

  const getMilestoneTargetCount = () => {
    const trimmed = String(numberOfMilestones || '').trim();
    if (!/^\d+$/.test(trimmed)) return null;
    const parsed = Number(trimmed);
    return parsed > 0 ? parsed : null;
  };
  
  useEffect(() => {
    const userId = route?.params?.userId || route?.params?.user?.id || route?.params?.id || 1;
    fetchUser(userId);
    fetchCourses();
  }, [route]);

  useEffect(() => {
    if (!getMilestoneTargetCount() && automateMilestoneDates) {
      setAutomateMilestoneDates(false);
    }
  }, [numberOfMilestones, automateMilestoneDates]);
  
  
  const fetchUser = async (userId) => {
    try {
      const response = await fetch(`${getApiUrl()}/api/users/${userId}`);
      if (response.ok) {
        const data = await response.json();
        setCurrentUser(data);
        setIsUSUserFromUser(data);
      }
    } catch (error) {
      console.error('Error fetching user:', error);
    }
  };
  
  const fetchCourses = async () => {
    try {
      const response = await fetch(`${getApiUrl()}/api/courses`);
      if (response.ok) {
        const data = await response.json();
        setCourses(dedupeCoursesForDropdown(Array.isArray(data) ? data : []));
      }
    } catch (error) {
      console.error('Error fetching courses:', error);
    }
  };
  
  const handleCourseSelect = async (course) => {
    setSelectedCourse(course);
    setShowCoursesDropdown(false);
    
    try {
      const typesUrl = exerciseTypesByCourseUrl(course.id, {
        viewerId: currentUser?.id,
        studentId: currentUser?.id,
      });
      const response = await fetch(typesUrl);
      if (response.ok) {
        const data = await response.json();
        setExerciseTypes(data);
        if (data && data.length > 0) {
          // Auto-select first exercise type if only one
          if (data.length === 1) {
            handleExerciseTypeSelect(data[0]);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching exercise types:', error);
    }
  };
  
  const handleExerciseTypeSelect = async (exerciseType) => {
    setSelectedExerciseType(exerciseType);
    
    try {
      const listUrl = exercisesListUrl({
        exerciseTypeId: exerciseType.id,
        courseId: selectedCourse?.id,
        studentId: currentUser?.id,
        viewerId: currentUser?.id,
      });
      const response = await fetch(listUrl);
      if (response.ok) {
        const data = await response.json();
        setExercises(data);
      }
    } catch (error) {
      console.error('Error fetching exercises:', error);
    }
  };
  
  const handleExerciseSelect = (exercise) => {
    setSelectedExercise(exercise);
    // Minimize exercise section
    setShowExerciseSection(false);
    setShowCoursesDropdown(false);
    setShowExercisesMenu(false);
    setShowGradeExercises(false);
    setShowAssignedExercises(false);
    setSelectedCourse(null);
    setSelectedExerciseType(null);
    setExercises([]);
  };
  
  const handleShowGradeExercises = async () => {
    if (!currentUser?.id) return;
    setShowGradeExercises(!showGradeExercises);
    
    if (!showGradeExercises) {
      try {
        const response = await fetch(`${getApiUrl()}/api/exercises/grade/${currentUser.id}`);
        if (response.ok) {
          const data = await response.json();
          setGradeExercises(data);
        }
      } catch (error) {
        console.error('Error fetching grade exercises:', error);
      }
    }
  };
  
  const handleShowAssignedExercises = async () => {
    if (!currentUser?.id) return;
    setShowAssignedExercises(!showAssignedExercises);
    
    if (!showAssignedExercises) {
      try {
        const response = await fetch(`${getApiUrl()}/api/training-session-exercises/assigned/${currentUser.id}`);
        if (response.ok) {
          const data = await response.json();
          setAssignedExercises(data);
        }
      } catch (error) {
        console.error('Error fetching assigned exercises:', error);
      }
    }
  };
  
  const handleCoursesTilePress = () => {
    if (selectedExerciseType || exercises.length > 0) {
      flashTileRed(coursesTileColorAnim);
      return;
    }
    
    if (showExercisesMenu) {
      setShowExercisesMenu(false);
      setShowGradeExercises(false);
      setShowAssignedExercises(false);
    }
    
    setShowCoursesDropdown(!showCoursesDropdown);
  };
  
  const handleExercisesTilePress = () => {
    if (selectedCourse || selectedExerciseType || exercises.length > 0) {
      flashTileRed(exercisesTileColorAnim);
      return;
    }
    
    if (showCoursesDropdown) {
      setShowCoursesDropdown(false);
    }
    if (showGoalsTable) {
      setShowGoalsTable(false);
      setExpandedGoalRowId(null);
    }
    
    setShowExercisesMenu(!showExercisesMenu);
  };

  const fetchGoals = async () => {
    setLoadingGoals(true);
    try {
      const response = await fetch(`${getApiUrl()}/api/goals`);
      if (!response.ok) {
        throw new Error('Failed to fetch goals');
      }
      const data = await response.json();
      setGoalsList(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching goals:', error);
      setGoalsList([]);
    } finally {
      setLoadingGoals(false);
    }
  };

  const fetchMilestonesForGoal = async (goalId) => {
    setLoadingGoalMilestonesId(goalId);
    try {
      const response = await fetch(`${getApiUrl()}/api/milestones?goal_id=${goalId}`);
      if (!response.ok) {
        throw new Error('Failed to fetch milestones');
      }

      const data = await response.json();
      const sortedMilestones = (Array.isArray(data) ? data : []).sort((a, b) => {
        const aOrder = Number.isFinite(a.order_index) ? a.order_index : 0;
        const bOrder = Number.isFinite(b.order_index) ? b.order_index : 0;
        return aOrder - bOrder;
      });

      setGoalMilestonesByGoal((prev) => ({
        ...prev,
        [goalId]: sortedMilestones,
      }));

      return sortedMilestones;
    } catch (error) {
      console.error('Error fetching milestones for goal:', error);
      setGoalMilestonesByGoal((prev) => ({
        ...prev,
        [goalId]: [],
      }));
      return [];
    } finally {
      setLoadingGoalMilestonesId(null);
    }
  };

  const handleGoalsTilePress = async () => {
    if (selectedCourse || selectedExerciseType || exercises.length > 0) {
      flashTileRed(goalsTileColorAnim);
      return;
    }

    if (showCoursesDropdown) {
      setShowCoursesDropdown(false);
    }
    if (showExercisesMenu) {
      setShowExercisesMenu(false);
      setShowGradeExercises(false);
      setShowAssignedExercises(false);
    }

    const nextShowGoalsTable = !showGoalsTable;
    setShowGoalsTable(nextShowGoalsTable);
    if (!nextShowGoalsTable) {
      setExpandedGoalRowId(null);
      return;
    }

    await fetchGoals();
  };

  const formatDateForField = (dateValue) => {
    if (!dateValue) return '';
    const parsedDate = new Date(dateValue);
    if (Number.isNaN(parsedDate.getTime())) return '';
    return formatDateForInput(parsedDate);
  };

  const handleGoalRowPress = async (goal) => {
    if (expandedGoalRowId === goal.id) {
      setExpandedGoalRowId(null);
      return;
    }

    setExpandedGoalRowId(goal.id);
    if (!goalMilestonesByGoal[goal.id]) {
      await fetchMilestonesForGoal(goal.id);
    }
  };

  const handleSelectGoal = async (goal) => {
    const milestonesForGoal = goalMilestonesByGoal[goal.id] || (await fetchMilestonesForGoal(goal.id));
    const mappedMilestones = milestonesForGoal.map((milestone, index) => ({
      id: milestone.id || Date.now() + index,
      name: milestone.name || '',
      targetValue: milestone.target_value !== null && milestone.target_value !== undefined
        ? String(milestone.target_value)
        : '',
      targetUnit: milestone.target_unit || null,
      milestoneTargetDate: formatDateForField(milestone.milestone_target_date),
      isCompleted: Boolean(milestone.is_completed),
      completedDate: formatDateForField(milestone.completed_date),
      orderIndex: Number.isFinite(milestone.order_index) ? milestone.order_index : index,
    }));

    const sortedMappedMilestones = [...mappedMilestones].sort((a, b) => a.orderIndex - b.orderIndex);

    const nextExpandedMilestones = {};
    sortedMappedMilestones.forEach((milestone, index) => {
      nextExpandedMilestones[milestone.id] = index === 0;
    });

    setGoalName(goal.goal_name || '');
    setSelectedExercise(
      goal.exercise_id
        ? {
            id: goal.exercise_id,
            exercise_name: goal.exercise_name || 'Selected Exercise',
          }
        : null
    );
    setStatus(goal.status || 'not started');
    setGoalCategory(Array.isArray(goal.goal_category) ? goal.goal_category : []);
    setGoalType(goal.goal_type || null);
    setNumberOfMilestones(
      goal.number_of_milestones !== null && goal.number_of_milestones !== undefined
        ? String(goal.number_of_milestones)
        : ''
    );
    setEstimatedGoalCompletionDate(formatDateForField(goal.estimated_goal_completion_date));
    setAutomateMilestoneDates(false);
    setMilestones(sortedMappedMilestones);
    setMilestoneAnchorOffsets({});
    setExpandedMilestones(nextExpandedMilestones);
    setOpenTargetUnitDropdown(null);

    setShowExerciseSection(false);
    setShowCoursesDropdown(false);
    setShowExercisesMenu(false);
    setShowGradeExercises(false);
    setShowAssignedExercises(false);
    setShowGoalsTable(false);
    setExpandedGoalRowId(null);
    setShowMilestoneSection(true);
  };
  
  const flashTileRed = (animRef) => {
    Animated.sequence([
      Animated.timing(animRef, {
        toValue: 0,
        duration: 0,
        useNativeDriver: false,
      }),
      Animated.timing(animRef, {
        toValue: 1,
        duration: 1500,
        useNativeDriver: false,
      }),
    ]).start();
  };
  
  const toggleGoalCategory = (category) => {
    const newCategories = [...goalCategory];
    const index = newCategories.indexOf(category);
    if (index > -1) {
      newCategories.splice(index, 1);
    } else {
      newCategories.push(category);
    }
    setGoalCategory(newCategories);
  };

  const addMonthsToDate = (baseDate, monthsToAdd) => {
    const result = new Date(baseDate);
    const day = result.getDate();
    result.setDate(1);
    result.setMonth(result.getMonth() + monthsToAdd);
    const lastDayOfMonth = new Date(result.getFullYear(), result.getMonth() + 1, 0).getDate();
    result.setDate(Math.min(day, lastDayOfMonth));
    return result;
  };

  const formatDateForInput = (dateObj) => {
    const useUS = isUSUser || isUSUserFlag(currentUser);
    return formatDateObjectForInput(dateObj, useUS);
  };

  const getAutoMilestoneTargetDate = (milestoneNumber, totalMilestoneCount) => {
    const estimatedDateIso = parseDate(estimatedGoalCompletionDate);
    if (!estimatedDateIso || !totalMilestoneCount || totalMilestoneCount <= 0) {
      return '';
    }

    const today = new Date();
    const estimatedDate = new Date(estimatedDateIso);
    const totalMonthsToGoal = Math.max(
      1,
      (estimatedDate.getFullYear() - today.getFullYear()) * 12 +
        (estimatedDate.getMonth() - today.getMonth())
    );
    const monthsPerMilestone = totalMonthsToGoal / totalMilestoneCount;
    const milestoneMonthsFromNow = Math.max(1, Math.round(monthsPerMilestone * milestoneNumber));
    const autoDate = addMonthsToDate(today, milestoneMonthsFromNow);
    return formatDateForInput(autoDate);
  };

  const createMilestoneDraft = (orderIndex, totalMilestoneCount, useAutomation = automateMilestoneDates) => ({
    id: Date.now() + Math.floor(Math.random() * 100000) + orderIndex,
    name: '',
    targetValue: '',
    targetUnit: null,
    milestoneTargetDate: useAutomation
      ? getAutoMilestoneTargetDate(orderIndex + 1, totalMilestoneCount)
      : '',
    isCompleted: false,
    completedDate: '',
    orderIndex,
  });
  
  const addMilestone = () => {
    try {
      console.log('addMilestone called, current milestones count:', milestones.length);
      const totalMilestoneCount = getMilestoneTargetCount() || milestones.length + 1;
      const newMilestone = createMilestoneDraft(milestones.length, totalMilestoneCount);
      console.log('Adding new milestone:', newMilestone);
      setMilestones([...milestones, newMilestone]);
      setExpandedMilestones((prev) => ({ ...prev, [newMilestone.id]: true }));
      
      // Ensure milestone section is expanded
      if (!showMilestoneSection) {
        setShowMilestoneSection(true);
      }
      
      // Scroll main page to show new milestone
      setTimeout(() => {
        if (scrollViewRef.current && milestoneSectionRef.current) {
          // Scroll to the milestone section
          scrollViewRef.current.scrollToEnd({ animated: true });
        }
      }, 300);
    } catch (error) {
      console.error('Error adding milestone:', error);
      Alert.alert('Error', 'Failed to add milestone. Please try again.');
    }
  };
  
  
  const removeMilestone = (id) => {
    const updated = milestones.filter(m => m.id !== id).map((m, index) => ({
      ...m,
      orderIndex: index,
    }));
    setMilestones(updated);
    setExpandedMilestones((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    if (openTargetUnitDropdown === id) {
      setOpenTargetUnitDropdown(null);
    }
  };

  const isMilestoneExpanded = (id) => expandedMilestones[id] !== false;

  const toggleMilestoneExpanded = (id) => {
    const nextExpanded = !isMilestoneExpanded(id);
    setExpandedMilestones((prev) => ({
      ...prev,
      [id]: nextExpanded,
    }));

    if (!nextExpanded && openTargetUnitDropdown === id) {
      setOpenTargetUnitDropdown(null);
    }
  };
  
  const updateMilestone = (id, field, value) => {
    const updated = milestones.map(m => 
      m.id === id ? { ...m, [field]: value } : m
    );
    setMilestones(updated);
  };
  
  const parseDate = (dateString) => {
    if (!dateString) return null;
    const useUS = isUSUser || isUSUserFlag(currentUser);
    const flexible = parseFlexibleDateInput(dateString, useUS);
    if (flexible) return flexible;
    
    // Try standard date parsing
    const parsed = new Date(dateString);
    return isNaN(parsed.getTime()) ? null : parsed.toISOString();
  };

  const isMilestoneRequiredFieldsComplete = (milestone) => {
    return Boolean(
      milestone &&
      milestone.name.trim() &&
      milestone.targetValue &&
      milestone.targetUnit &&
      milestone.milestoneTargetDate &&
      milestone.milestoneTargetDate.trim()
    );
  };

  const handleToggleAutomateMilestoneDates = () => {
    const targetMilestoneCount = getMilestoneTargetCount();
    const nextAutomateState = !automateMilestoneDates;
    setAutomateMilestoneDates(nextAutomateState);

    if (!nextAutomateState || !targetMilestoneCount) {
      return;
    }

    if (milestones.length > targetMilestoneCount) {
      // More milestones already exist than requested, ignore/do nothing.
      return;
    }

    let updatedMilestones = [...milestones];
    const milestonesToAdd = targetMilestoneCount - updatedMilestones.length;

    for (let i = 0; i < milestonesToAdd; i += 1) {
      updatedMilestones.push(
        createMilestoneDraft(updatedMilestones.length, targetMilestoneCount, true)
      );
    }

    // Ensure empty milestone dates are auto-filled when automation is enabled.
    updatedMilestones = updatedMilestones.map((milestone, index) => {
      if (milestone.milestoneTargetDate && milestone.milestoneTargetDate.trim()) {
        return milestone;
      }
      return {
        ...milestone,
        milestoneTargetDate: getAutoMilestoneTargetDate(index + 1, targetMilestoneCount),
      };
    });

    setMilestones(updatedMilestones);

    const nextExpandedMilestones = {};
    updatedMilestones.forEach((milestone, index) => {
      nextExpandedMilestones[milestone.id] = index === 0;
    });
    setExpandedMilestones(nextExpandedMilestones);
    setOpenTargetUnitDropdown(null);
    setShowMilestoneSection(true);
  };

  const handleMilestoneChevronPress = (milestoneIndex) => {
    if (milestoneIndex < 0 || milestoneIndex >= milestones.length) {
      return;
    }

    const targetMilestone = milestones[milestoneIndex];
    if (!targetMilestone) {
      return;
    }

    const nextExpandedMilestones = {};
    milestones.forEach((milestone, index) => {
      nextExpandedMilestones[milestone.id] = index === milestoneIndex;
    });

    setShowMilestoneSection(true);
    setExpandedMilestones(nextExpandedMilestones);
    setOpenTargetUnitDropdown(null);
    setPendingMilestoneScroll({ id: targetMilestone.id, attempt: 0 });
  };

  useEffect(() => {
    if (!pendingMilestoneScroll) {
      return undefined;
    }

    const delayMs = 120 + pendingMilestoneScroll.attempt * 60;
    const timer = setTimeout(() => {
      if (Platform.OS === 'web' && typeof document !== 'undefined') {
        const milestoneElement = document.querySelector(
          `[data-testid="goal-milestone-${pendingMilestoneScroll.id}"]`
        );

        if (milestoneElement && typeof milestoneElement.scrollIntoView === 'function') {
          milestoneElement.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'nearest',
          });
          setPendingMilestoneScroll(null);
          return;
        }
      }

      const milestoneOffset = milestoneAnchorOffsets[pendingMilestoneScroll.id];
      if (typeof milestoneOffset === 'number') {
        const targetScrollY = Math.max(0, milestoneSectionOffsetY + milestoneOffset - 12);
        if (scrollViewRef.current?.scrollTo) {
          scrollViewRef.current.scrollTo({ y: targetScrollY, animated: true });
        }

        // Run one follow-up pass so we land correctly after layout settles.
        if (pendingMilestoneScroll.attempt < 1) {
          setPendingMilestoneScroll({
            id: pendingMilestoneScroll.id,
            attempt: pendingMilestoneScroll.attempt + 1,
          });
        } else {
          setPendingMilestoneScroll(null);
        }
        return;
      }

      if (pendingMilestoneScroll.attempt < 5) {
        setPendingMilestoneScroll({
          id: pendingMilestoneScroll.id,
          attempt: pendingMilestoneScroll.attempt + 1,
        });
      } else {
        setPendingMilestoneScroll(null);
      }
    }, delayMs);

    return () => clearTimeout(timer);
  }, [pendingMilestoneScroll, milestoneAnchorOffsets, milestoneSectionOffsetY]);
  
  const handleRefresh = () => {
    setShowRefreshModal(true);
  };
  
  const confirmRefresh = () => {
    setGoalName('');
    setSelectedExercise(null);
    setStatus('not started');
    setGoalCategory([]);
    setGoalType(null);
    setNumberOfMilestones('');
    setEstimatedGoalCompletionDate('');
    setAutomateMilestoneDates(false);
    setMilestones([]);
    setMilestoneAnchorOffsets({});
    setPendingMilestoneScroll(null);
    setExpandedMilestones({});
    setShowExerciseSection(false);
    setShowMilestoneSection(false);
    setShowCoursesDropdown(false);
    setShowExercisesMenu(false);
    setShowGoalsTable(false);
    setShowGradeExercises(false);
    setShowAssignedExercises(false);
    setSelectedCourse(null);
    setSelectedExerciseType(null);
    setExercises([]);
    setShowRefreshModal(false);
  };
  
  const handleSave = () => {
    if (!goalName.trim()) {
      Alert.alert('Error', 'Please enter a goal name');
      return;
    }
    
    if (!selectedExercise) {
      Alert.alert('Error', 'Please select an exercise');
      return;
    }
    
    if (!goalType) {
      Alert.alert('Error', 'Please select a goal type');
      return;
    }
    
    // Validate milestones if they exist
    if (milestones.length > 0) {
      const invalidMilestones = milestones.some(m => 
        !m.name.trim() || !m.targetValue || !m.targetUnit || !m.milestoneTargetDate
      );
      
      if (invalidMilestones) {
        Alert.alert('Error', 'Please fill in all required fields for all milestones');
        return;
      }
    }
    
    // Show modal - different message based on whether milestones exist
    setShowSaveModal(true);
  };
  
  const confirmSave = async () => {
    setShowSaveModal(false);
    setSaving(true);
    
    try {
      const safeMilestoneCount = getMilestoneTargetCount() || milestones.length;
      const estimatedDateIso = parseDate(estimatedGoalCompletionDate);

      // Create goal
      const goalData = {
        goal_name: goalName.trim(),
        exercise_id: selectedExercise.id,
        status: status,
        goal_category: goalCategory,
        goal_type: goalType,
        number_of_milestones: safeMilestoneCount,
        estimated_goal_completion_date: estimatedDateIso ? estimatedDateIso.split('T')[0] : null,
      };
      
      const goalResponse = await fetch(`${getApiUrl()}/api/goals`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(goalData),
      });
      
      if (!goalResponse.ok) {
        const errorData = await goalResponse.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to create goal');
      }
      
      const goal = await goalResponse.json();
      
      // Create milestones if they exist
      if (milestones.length > 0) {
        for (const milestone of milestones) {
          const milestoneData = {
            goal_id: goal.id,
            name: milestone.name.trim(),
            order_index: milestone.orderIndex,
            is_completed: milestone.isCompleted,
            completed_date: milestone.completedDate ? parseDate(milestone.completedDate) : null,
            milestone_target_date: parseDate(milestone.milestoneTargetDate),
            target_value: parseFloat(milestone.targetValue),
            target_unit: milestone.targetUnit,
          };
          
          const milestoneResponse = await fetch(`${getApiUrl()}/api/milestones`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(milestoneData),
          });
          
          if (!milestoneResponse.ok) {
            const errorData = await milestoneResponse.json().catch(() => ({}));
            throw new Error(errorData.error || 'Failed to create milestone');
          }
        }
      }
      
      setSaving(false);
      
      if (milestones.length === 0) {
        // Show success message for goal without milestones
        Alert.alert('Success', 'Goal created successfully!');
      } else {
        // Show success message for goal with milestones
        Alert.alert('Success', 'Goal and associated Milestones created');
      }
      
      // Reset form
      confirmRefresh();
    } catch (error) {
      console.error('Error saving goal:', error);
      Alert.alert('Error', error.message || 'Failed to save goal. Please try again.');
      setSaving(false);
    }
  };

  const targetMilestoneCount = getMilestoneTargetCount();
  const progressChevronCount = targetMilestoneCount || 0;
  const completedChevronCount = milestones
    .slice(0, progressChevronCount)
    .filter((milestone) => isMilestoneRequiredFieldsComplete(milestone)).length;
  const remainingChevronCount = Math.max(progressChevronCount - completedChevronCount, 0);
  
  const styles = getStyles(theme);
  
  return (
    <View
      style={styles.container}
      {...(Platform.OS === 'web' ? { dataSet: { screen: 'goals' } } : {})}
    >
      <StatusBar style={theme.isDarkMode ? 'light' : 'dark'} />
      
      {/* Header */}
      <View style={styles.header}>
        <View style={styles.hamburgerContainer}>
          <HamburgerMenu navigation={navigation} stopwatchRef={stopwatchRef} />
        </View>
        <View style={styles.breadcrumbContainer}>
          <Text style={styles.breadcrumb}>Goals</Text>
        </View>
        <View style={styles.profileIconContainer}>
          <ProfileIcon navigation={navigation} user={currentUser} />
        </View>
      </View>
      
      <ScrollView 
        ref={scrollViewRef}
        style={styles.content} 
        contentContainerStyle={styles.contentContainer}
        scrollEnabled={true}
        showsVerticalScrollIndicator={true}
        nestedScrollEnabled={true}
        bounces={false}
        keyboardShouldPersistTaps="handled"
        alwaysBounceVertical={false}
        {...(Platform.OS === 'web' ? {
          dataSet: { scrollContainer: 'goals-page' },
        } : {})}
        onContentSizeChange={(contentWidth, contentHeight) => {
          // Content size changed - no special handling needed
        }}
      >
        {/* Title */}
        <Text style={styles.title}>Create Goal</Text>
        
        {/* Goal Name */}
        <View style={styles.formField}>
          <Text style={styles.label}>Goal Name *</Text>
          <TextInput
            style={styles.input}
            value={goalName}
            onChangeText={setGoalName}
            placeholder="Enter goal name"
            placeholderTextColor={theme.isDarkMode ? '#888' : '#999'}
          />
          <Text style={styles.helpText}>
            The goal name will literally be the goal itself. Which will likely be an exercise name and anything else you want.
          </Text>
        </View>
        
        {/* Exercise Section */}
        <View style={styles.sectionContainer}>
          <Pressable
            style={({ pressed }) => [
              styles.goalsDropdownTrigger,
              pressed && styles.goalsDropdownTriggerPressed,
            ]}
            onPress={() => setShowExerciseSection(!showExerciseSection)}
            {...(Platform.OS === 'web'
              ? { dataSet: { goalsDropdownTrigger: 'true' } }
              : {})}
          >
            <Text style={styles.sectionButtonText}>
              {selectedExercise ? selectedExercise.exercise_name : 'Select Exercise'}
            </Text>
            <Text style={styles.sectionArrow}>
              {showExerciseSection ? '▲' : '▼'}
            </Text>
          </Pressable>
          
          {showExerciseSection && (
            <View style={styles.sectionContent}>
              {/* Courses and Exercises Tiles */}
              <View style={styles.tilesRow}>
                <Animated.View style={[
                  styles.coursesTile,
                  {
                    backgroundColor: coursesTileColorAnim.interpolate({
                      inputRange: [0, 1],
                      outputRange: ['#ff0000', theme.isDarkMode ? theme.colors.surface : '#FFFFFF'],
                    }),
                  }
                ]}>
                  <Image
                    source={COURSES_IMAGE}
                    style={styles.coursesTileImage}
                    contentFit="contain"
                  />
                  <TouchableOpacity
                    style={styles.coursesTileButton}
                    onPress={handleCoursesTilePress}
                  >
                    <Text style={styles.coursesTileButtonText}>Courses</Text>
                  </TouchableOpacity>
                </Animated.View>
                
                <Animated.View style={[
                  styles.coursesTile,
                  {
                    backgroundColor: exercisesTileColorAnim.interpolate({
                      inputRange: [0, 1],
                      outputRange: ['#ff0000', theme.isDarkMode ? theme.colors.surface : '#FFFFFF'],
                    }),
                  }
                ]}>
                  <Image
                    source={EXERCISES_IMAGE}
                    style={styles.coursesTileImage}
                    contentFit="contain"
                  />
                  <TouchableOpacity
                    style={styles.coursesTileButton}
                    onPress={handleExercisesTilePress}
                  >
                    <Text style={styles.coursesTileButtonText}>Exercises</Text>
                  </TouchableOpacity>
                </Animated.View>

                <Animated.View style={[
                  styles.coursesTile,
                  {
                    backgroundColor: goalsTileColorAnim.interpolate({
                      inputRange: [0, 1],
                      outputRange: ['#ff0000', theme.isDarkMode ? theme.colors.surface : '#FFFFFF'],
                    }),
                  }
                ]}>
                  <Image
                    source={GOALS_IMAGE}
                    style={styles.coursesTileImage}
                    contentFit="contain"
                  />
                  <TouchableOpacity
                    style={styles.coursesTileButton}
                    onPress={handleGoalsTilePress}
                  >
                    <Text style={styles.coursesTileButtonText}>Goals</Text>
                  </TouchableOpacity>
                </Animated.View>
              </View>
              
              {/* Courses Dropdown */}
              {showCoursesDropdown && (
                <View
                  style={styles.dropdown}
                  {...(Platform.OS === 'web' ? { dataSet: { dropdownSurface: 'true' } } : {})}
                >
                  {Platform.OS === 'web' ? (
                    <View style={styles.dropdownScrollViewWeb}>
                      {courses.map((course) => (
                        <TouchableOpacity
                          key={course.id}
                          style={styles.dropdownItem}
                          onPress={() => handleCourseSelect(course)}
                          {...(Platform.OS === 'web' ? { dataSet: { dropdownRow: 'true' } } : {})}
                        >
                          <Text style={styles.dropdownItemText}>{course.course_name}</Text>
                        </TouchableOpacity>
                      ))}
                    </View>
                  ) : (
                    <FlatList
                      data={courses}
                      keyExtractor={(item) => item.id.toString()}
                      renderItem={({ item }) => (
                        <TouchableOpacity
                          style={styles.dropdownItem}
                          onPress={() => handleCourseSelect(item)}
                          {...(Platform.OS === 'web' ? { dataSet: { dropdownRow: 'true' } } : {})}
                        >
                          <Text style={styles.dropdownItemText}>{item.course_name}</Text>
                        </TouchableOpacity>
                      )}
                      style={styles.dropdownScrollView}
                      contentContainerStyle={styles.dropdownScrollContent}
                      showsVerticalScrollIndicator={true}
                      nestedScrollEnabled={true}
                      scrollEnabled={true}
                      bounces={false}
                    />
                  )}
                </View>
              )}
              
              {/* Exercise Types Dropdown */}
              {selectedCourse && exerciseTypes.length > 0 && (
                <View
                  style={styles.dropdown}
                  {...(Platform.OS === 'web' ? { dataSet: { dropdownSurface: 'true' } } : {})}
                >
                  <Text style={styles.dropdownTitle}>Select Exercise Type:</Text>
                  <FlatList
                    data={exerciseTypes}
                    keyExtractor={(item) => item.id.toString()}
                    renderItem={({ item }) => (
                      <TouchableOpacity
                        style={styles.dropdownItem}
                        onPress={() => handleExerciseTypeSelect(item)}
                        {...(Platform.OS === 'web' ? { dataSet: { dropdownRow: 'true' } } : {})}
                      >
                        <Text style={styles.dropdownItemText}>{item.exercise_type_name}</Text>
                      </TouchableOpacity>
                    )}
                    style={styles.dropdownScrollView}
                    contentContainerStyle={styles.dropdownScrollContent}
                    showsVerticalScrollIndicator={true}
                    nestedScrollEnabled={true}
                    scrollEnabled={true}
                    bounces={false}
                  />
                </View>
              )}
              
              {/* Exercises Dropdown */}
              {selectedExerciseType && exercises.length > 0 && (
                <View style={styles.dropdown}>
                  <Text style={styles.dropdownTitle}>Select Exercise:</Text>
                  <FlatList
                    data={exercises}
                    keyExtractor={(item) => item.id.toString()}
                    renderItem={({ item }) => (
                      <TouchableOpacity
                        style={styles.exerciseTile}
                        onPress={() => handleExerciseSelect(item)}
                      >
                        <Text style={styles.exerciseName}>{item.exercise_name}</Text>
                      </TouchableOpacity>
                    )}
                    style={styles.dropdownScrollView}
                    contentContainerStyle={styles.dropdownScrollContent}
                    showsVerticalScrollIndicator={true}
                    nestedScrollEnabled={true}
                    scrollEnabled={true}
                    bounces={false}
                  />
                </View>
              )}
              
              {/* Exercises Menu */}
              {showExercisesMenu && (
                <View style={styles.dropdown}>
                  <ScrollView 
                    style={styles.dropdownScrollView} 
                    nestedScrollEnabled={true}
                    showsVerticalScrollIndicator={true}
                    contentContainerStyle={styles.dropdownScrollContent}
                    scrollEnabled={true}
                    bounces={false}
                  >
                    {/* Grade Exercises */}
                    <TouchableOpacity
                      style={styles.menuHeader}
                      onPress={handleShowGradeExercises}
                    >
                      <Text style={styles.menuHeaderText}>Grade Exercises</Text>
                      <Text style={styles.menuArrow}>{showGradeExercises ? '▼' : '▶'}</Text>
                    </TouchableOpacity>
                    {showGradeExercises && (
                      <View style={styles.subMenu}>
                        {gradeExercises.map((exercise) => (
                          <TouchableOpacity
                            key={exercise.id}
                            style={styles.exerciseTile}
                            onPress={() => handleExerciseSelect(exercise)}
                          >
                            <Text style={styles.exerciseName}>{exercise.exercise_name}</Text>
                          </TouchableOpacity>
                        ))}
                      </View>
                    )}
                    
                    {/* Assigned Exercises */}
                    <TouchableOpacity
                      style={styles.menuHeader}
                      onPress={handleShowAssignedExercises}
                    >
                      <Text style={styles.menuHeaderText}>Assigned Exercises</Text>
                      <Text style={styles.menuArrow}>{showAssignedExercises ? '▼' : '▶'}</Text>
                    </TouchableOpacity>
                    {showAssignedExercises && (
                      <View style={styles.subMenu}>
                        {assignedExercises.map((exercise) => (
                          <TouchableOpacity
                            key={exercise.id}
                            style={styles.exerciseTile}
                            onPress={() => handleExerciseSelect(exercise)}
                          >
                            <Text style={styles.exerciseName}>{exercise.exercise_name}</Text>
                          </TouchableOpacity>
                        ))}
                      </View>
                    )}
                  </ScrollView>
                </View>
              )}

              {/* Goals Table */}
              {showGoalsTable && (
                <View style={styles.goalsTableContainer}>
                  <View style={styles.goalsTableHeader}>
                    <Text style={[styles.goalsTableHeaderText, styles.goalsTableGoalCol]}>Goal</Text>
                    <Text style={[styles.goalsTableHeaderText, styles.goalsTableExerciseCol]}>Exercise</Text>
                    <Text style={[styles.goalsTableHeaderText, styles.goalsTableSelectCol]}>Action</Text>
                  </View>

                  {loadingGoals ? (
                    <Text style={styles.goalsTableEmptyText}>Loading goals...</Text>
                  ) : goalsList.length === 0 ? (
                    <Text style={styles.goalsTableEmptyText}>No goals found</Text>
                  ) : (
                    <ScrollView
                      style={styles.goalsTableScroll}
                      contentContainerStyle={styles.goalsTableScrollContent}
                      nestedScrollEnabled
                    >
                      {goalsList.map((goal) => {
                        const isExpanded = expandedGoalRowId === goal.id;
                        const rowMilestones = goalMilestonesByGoal[goal.id] || [];
                        const isLoadingMilestones = loadingGoalMilestonesId === goal.id;

                        return (
                          <View key={goal.id} style={styles.goalsTableRowBlock}>
                            <TouchableOpacity
                              style={[
                                styles.goalsTableRow,
                                isExpanded && styles.goalsTableRowExpanded,
                              ]}
                              onPress={() => handleGoalRowPress(goal)}
                              activeOpacity={0.8}
                            >
                              <Text style={[styles.goalsTableCellText, styles.goalsTableGoalCol]} numberOfLines={1}>
                                {goal.goal_name || 'Untitled Goal'}
                              </Text>
                              <Text style={[styles.goalsTableCellText, styles.goalsTableExerciseCol]} numberOfLines={1}>
                                {goal.exercise_name || 'No exercise'}
                              </Text>
                              <TouchableOpacity
                                style={styles.goalsSelectButton}
                                onPress={(event) => {
                                  event?.stopPropagation?.();
                                  handleSelectGoal(goal);
                                }}
                              >
                                <Text style={styles.goalsSelectButtonText}>Select</Text>
                              </TouchableOpacity>
                            </TouchableOpacity>

                            {isExpanded && (
                              <View style={styles.goalsTableRowDetails}>
                                <Text style={styles.goalsDetailsTitle}>Goal: {goal.goal_name || 'Untitled Goal'}</Text>
                                {isLoadingMilestones ? (
                                  <Text style={styles.goalsDetailsText}>Loading milestones...</Text>
                                ) : rowMilestones.length === 0 ? (
                                  <Text style={styles.goalsDetailsText}>No associated milestones</Text>
                                ) : (
                                  rowMilestones.map((milestone, index) => (
                                    <Text key={`${goal.id}-milestone-${milestone.id || index}`} style={styles.goalsDetailsText}>
                                      Milestone {index + 1}: {milestone.name || 'Untitled'}{milestone.milestone_target_date ? ` (${formatDateForField(milestone.milestone_target_date)})` : ''}
                                    </Text>
                                  ))
                                )}
                              </View>
                            )}
                          </View>
                        );
                      })}
                    </ScrollView>
                  )}
                </View>
              )}
            </View>
          )}
        </View>
        
        {/* Exercise not exist button */}
        <TouchableOpacity
          style={styles.createExerciseButton}
          onPress={() => navigation.navigate('ExerciseBuilder', { userId: currentUser?.id || 1, user: currentUser })}
        >
          <Text style={styles.createExerciseButtonText}>
            Exercise not exist? Create new Exercise here
          </Text>
        </TouchableOpacity>
        
        {/* Status Picklist */}
        <View style={[styles.formField, showStatusDropdown && styles.formFieldDropdownOpen]}>
          <Text style={styles.label}>Status *</Text>
          <Pressable
            style={({ pressed }) => [
              styles.goalsDropdownTrigger,
              pressed && styles.goalsDropdownTriggerPressed,
            ]}
            onPress={() => {
              setShowStatusDropdown(!showStatusDropdown);
              setShowGoalCategoryDropdown(false);
              setShowGoalTypeDropdown(false);
            }}
            {...(Platform.OS === 'web'
              ? { dataSet: { goalsDropdownTrigger: 'true' } }
              : {})}
          >
            <Text style={styles.dropdownButtonText}>
              {status ? capitalizeFirst(status) : 'Select Status'}
            </Text>
            <Text style={styles.dropdownArrow}>
              {showStatusDropdown ? '▲' : '▼'}
            </Text>
          </Pressable>
          
          {showStatusDropdown && (
            <>
              {Platform.OS === 'web' && (
                <TouchableOpacity
                  style={styles.dropdownOverlay}
                  activeOpacity={1}
                  onPress={() => setShowStatusDropdown(false)}
                />
              )}
              <View
                style={styles.dropdownMenu}
                {...(Platform.OS === 'web' ? { dataSet: { dropdownSurface: 'true' } } : {})}
              >
                <ScrollView style={styles.dropdownScrollView}>
                  {STATUS_OPTIONS.map((option) => (
                    <TouchableOpacity
                      key={option}
                      style={styles.dropdownOption}
                      onPress={() => {
                        setStatus(option);
                        setShowStatusDropdown(false);
                      }}
                      {...(Platform.OS === 'web' ? { dataSet: { dropdownRow: 'true' } } : {})}
                    >
                      <Text style={styles.dropdownOptionText}>{capitalizeFirst(option)}</Text>
                    </TouchableOpacity>
                  ))}
                </ScrollView>
              </View>
            </>
          )}
        </View>
        
        {/* Goal Category Picklist (Multi-select) */}
        <View style={[styles.formField, showGoalCategoryDropdown && styles.formFieldDropdownOpen]}>
          <Text style={styles.label}>Goal Category</Text>
          <Pressable
            style={({ pressed }) => [
              styles.goalsDropdownTrigger,
              pressed && styles.goalsDropdownTriggerPressed,
            ]}
            onPress={() => {
              setShowGoalCategoryDropdown(!showGoalCategoryDropdown);
              setShowStatusDropdown(false);
              setShowGoalTypeDropdown(false);
            }}
            {...(Platform.OS === 'web'
              ? { dataSet: { goalsDropdownTrigger: 'true' } }
              : {})}
          >
            <Text style={styles.dropdownButtonText}>
              {goalCategory.length > 0 ? goalCategory.join(', ') : 'Select Goal Category'}
            </Text>
            <Text style={styles.dropdownArrow}>
              {showGoalCategoryDropdown ? '▲' : '▼'}
            </Text>
          </Pressable>
          
          {showGoalCategoryDropdown && (
            <>
              {Platform.OS === 'web' && (
                <TouchableOpacity
                  style={styles.dropdownOverlay}
                  activeOpacity={1}
                  onPress={() => setShowGoalCategoryDropdown(false)}
                />
              )}
              <View
                style={styles.dropdownMenu}
                {...(Platform.OS === 'web' ? { dataSet: { dropdownSurface: 'true' } } : {})}
              >
                <ScrollView style={styles.dropdownScrollView}>
                  {GOAL_CATEGORY_OPTIONS.map((option) => {
                    const isSelected = goalCategory.includes(option);
                    return (
                      <TouchableOpacity
                        key={option}
                        style={styles.dropdownOption}
                        onPress={() => toggleGoalCategory(option)}
                        {...(Platform.OS === 'web' ? { dataSet: { dropdownRow: 'true' } } : {})}
                      >
                        <View style={styles.checkboxContainer}>
                          <View style={[
                            styles.checkbox,
                            isSelected && styles.checkboxSelected
                          ]}>
                            {isSelected && <Text style={styles.checkmark}>✓</Text>}
                          </View>
                          <Text style={styles.dropdownOptionText}>{option}</Text>
                        </View>
                      </TouchableOpacity>
                    );
                  })}
                </ScrollView>
              </View>
            </>
          )}
        </View>
        
        {/* Goal Type Picklist */}
        <View style={[styles.formField, showGoalTypeDropdown && styles.formFieldDropdownOpen]}>
          <Text style={styles.label}>Goal Type *</Text>
          <Pressable
            style={({ pressed }) => [
              styles.goalsDropdownTrigger,
              pressed && styles.goalsDropdownTriggerPressed,
            ]}
            onPress={() => {
              setShowGoalTypeDropdown(!showGoalTypeDropdown);
              setShowStatusDropdown(false);
              setShowGoalCategoryDropdown(false);
            }}
            {...(Platform.OS === 'web'
              ? { dataSet: { goalsDropdownTrigger: 'true' } }
              : {})}
          >
            <Text style={styles.dropdownButtonText}>
              {goalType || 'Select Goal Type'}
            </Text>
            <Text style={styles.dropdownArrow}>
              {showGoalTypeDropdown ? '▲' : '▼'}
            </Text>
          </Pressable>
          
          {showGoalTypeDropdown && (
            <>
              {Platform.OS === 'web' && (
                <TouchableOpacity
                  style={styles.dropdownOverlay}
                  activeOpacity={1}
                  onPress={() => setShowGoalTypeDropdown(false)}
                />
              )}
              <View
                style={styles.dropdownMenu}
                {...(Platform.OS === 'web' ? { dataSet: { dropdownSurface: 'true' } } : {})}
              >
                <ScrollView style={styles.dropdownScrollView}>
                  {GOAL_TYPE_OPTIONS.map((option) => (
                    <TouchableOpacity
                      key={option}
                      style={styles.dropdownOption}
                      onPress={() => {
                        setGoalType(option);
                        setShowGoalTypeDropdown(false);
                      }}
                      {...(Platform.OS === 'web' ? { dataSet: { dropdownRow: 'true' } } : {})}
                    >
                      <Text style={styles.dropdownOptionText}>{option}</Text>
                    </TouchableOpacity>
                  ))}
                </ScrollView>
              </View>
            </>
          )}
        </View>
        
        {/* Number of Milestones */}
        <View style={styles.formField}>
          <Text style={styles.label}>Number of Milestones</Text>
          <TextInput
            style={styles.input}
            value={numberOfMilestones}
            onChangeText={setNumberOfMilestones}
            placeholder="Enter milestone estimate"
            placeholderTextColor={theme.isDarkMode ? '#888' : '#999'}
            keyboardType="numeric"
          />
          <Text style={styles.helpText}>
            This number is just an estimation, you can create more manually.
          </Text>
          <Text style={styles.helpText}>
            On creation of a goal, a user can add how many milestones you want (you can create more but this is involved in the calculation).
          </Text>
        </View>
        
        {/* Estimated Goal Completion Date */}
        <View style={styles.formField}>
          <Text style={styles.label}>Estimated Goal Completion Date</Text>
          <TextInput
            style={styles.input}
            value={estimatedGoalCompletionDate}
            onChangeText={setEstimatedGoalCompletionDate}
            placeholder="e.g., Jan 2027 or 15/01/2027"
            placeholderTextColor={theme.isDarkMode ? '#888' : '#999'}
          />
          <Text style={styles.helpText}>
            If Automate my Milestone dates is on, this date and Number of Milestones are used to prefill each milestone target date.
          </Text>
        </View>

        {/* Automate Milestone Dates */}
        {targetMilestoneCount && (
          <View style={styles.formField}>
            <TouchableOpacity
              style={styles.checkboxContainer}
              onPress={handleToggleAutomateMilestoneDates}
            >
              <View style={[styles.checkbox, automateMilestoneDates && styles.checkboxSelected]}>
                {automateMilestoneDates && <Text style={styles.checkmark}>✓</Text>}
              </View>
              <Text style={styles.label}>Automate my Milestone dates</Text>
            </TouchableOpacity>
          </View>
        )}
        
        {/* Milestone Section */}
        <View style={[styles.sectionContainer, styles.milestoneSectionContainer]} data-milestone-section="true">
          <Pressable
            style={({ pressed }) => [
              styles.goalsDropdownTrigger,
              pressed && styles.goalsDropdownTriggerPressed,
            ]}
            onPress={() => setShowMilestoneSection(!showMilestoneSection)}
            {...(Platform.OS === 'web'
              ? { dataSet: { goalsDropdownTrigger: 'true' } }
              : {})}
          >
            <Text style={styles.sectionButtonText}>Milestones</Text>
            <Text style={styles.sectionArrow}>
              {showMilestoneSection ? '▲' : '▼'}
            </Text>
          </Pressable>
          
          {showMilestoneSection && (
            <View
              style={styles.sectionContent}
              ref={milestoneSectionRef}
              onLayout={(event) => {
                setMilestoneSectionOffsetY(event.nativeEvent.layout.y);
              }}
            >
              {milestones.map((milestone, index) => {
                const isExpanded = isMilestoneExpanded(milestone.id);
                return (
                <View 
                  key={milestone.id} 
                  style={styles.milestoneForm}
                  testID={`goal-milestone-${milestone.id}`}
                  onLayout={(event) => {
                    const y = event.nativeEvent.layout.y;
                    setMilestoneAnchorOffsets((prev) => {
                      if (prev[milestone.id] === y) return prev;
                      return { ...prev, [milestone.id]: y };
                    });
                  }}
                >
                  <TouchableOpacity
                    style={styles.milestoneHeader}
                    onPress={() => toggleMilestoneExpanded(milestone.id)}
                    activeOpacity={0.7}
                  >
                    <View style={styles.milestoneHeaderLeft}>
                      <Text style={styles.milestoneExpandIcon}>
                        {isExpanded ? '▼' : '▶'}
                      </Text>
                      <Text style={styles.milestoneNumber}>Milestone {index + 1}</Text>
                    </View>
                    <TouchableOpacity
                      style={styles.deleteButton}
                      onPress={(event) => {
                        event?.stopPropagation?.();
                        removeMilestone(milestone.id);
                      }}
                    >
                      <Text style={styles.deleteButtonText}>🗑️</Text>
                    </TouchableOpacity>
                  </TouchableOpacity>
                  {isExpanded && (
                    <>
                      {/* Name */}
                      <View style={styles.formField}>
                        <Text style={styles.label}>Name *</Text>
                        <TextInput
                          style={styles.input}
                          value={milestone.name}
                          onChangeText={(text) => updateMilestone(milestone.id, 'name', text)}
                          placeholder="Enter milestone name"
                          placeholderTextColor={theme.isDarkMode ? '#888' : '#999'}
                        />
                      </View>
                      
                      {/* Target Value */}
                      <View style={styles.formField}>
                        <Text style={styles.label}>Target Value *</Text>
                        <TextInput
                          style={styles.input}
                          value={milestone.targetValue}
                          onChangeText={(text) => updateMilestone(milestone.id, 'targetValue', text)}
                          placeholder="Enter target value"
                          placeholderTextColor={theme.isDarkMode ? '#888' : '#999'}
                          keyboardType="numeric"
                        />
                      </View>
                      
                      {/* Target Unit */}
                      <View style={[styles.formField, openTargetUnitDropdown === milestone.id && styles.formFieldDropdownOpen]}>
                        <Text style={styles.label}>Target Unit *</Text>
                        <Pressable
                          style={({ pressed }) => [
                            styles.goalsDropdownTrigger,
                            pressed && styles.goalsDropdownTriggerPressed,
                          ]}
                          onPress={() => {
                            setOpenTargetUnitDropdown(openTargetUnitDropdown === milestone.id ? null : milestone.id);
                          }}
                          {...(Platform.OS === 'web'
                            ? { dataSet: { goalsDropdownTrigger: 'true' } }
                            : {})}
                        >
                          <Text style={styles.dropdownButtonText}>
                            {milestone.targetUnit || 'Select Target Unit'}
                          </Text>
                          <Text style={styles.dropdownArrow}>
                            {openTargetUnitDropdown === milestone.id ? '▲' : '▼'}
                          </Text>
                        </Pressable>
                        
                        {openTargetUnitDropdown === milestone.id && (
                          <>
                            {Platform.OS === 'web' && (
                              <TouchableOpacity
                                style={styles.dropdownOverlay}
                                activeOpacity={1}
                                onPress={() => setOpenTargetUnitDropdown(null)}
                              />
                            )}
                            <View
                              style={styles.dropdownMenu}
                              {...(Platform.OS === 'web' ? { dataSet: { dropdownSurface: 'true' } } : {})}
                            >
                              <ScrollView style={styles.dropdownScrollView}>
                                {TARGET_UNIT_OPTIONS.map((unit) => (
                                  <TouchableOpacity
                                    key={unit}
                                    style={styles.dropdownOption}
                                    onPress={() => {
                                      updateMilestone(milestone.id, 'targetUnit', unit);
                                      setOpenTargetUnitDropdown(null);
                                    }}
                                    {...(Platform.OS === 'web' ? { dataSet: { dropdownRow: 'true' } } : {})}
                                  >
                                    <Text style={styles.dropdownOptionText}>{unit}</Text>
                                  </TouchableOpacity>
                                ))}
                              </ScrollView>
                            </View>
                          </>
                        )}
                      </View>
                      
                      {/* Milestone Target Date */}
                      <View style={styles.formField}>
                        <Text style={styles.label}>Milestone Target Date *</Text>
                        <TextInput
                          style={styles.input}
                          value={milestone.milestoneTargetDate}
                          onChangeText={(text) => updateMilestone(milestone.id, 'milestoneTargetDate', text)}
                          placeholder="e.g., Jan 2025 or 15/01/2025"
                          placeholderTextColor={theme.isDarkMode ? '#888' : '#999'}
                        />
                      </View>
                      
                      {/* Is Completed */}
                      <View style={styles.formField}>
                        <TouchableOpacity
                          style={styles.checkboxContainer}
                          onPress={() => updateMilestone(milestone.id, 'isCompleted', !milestone.isCompleted)}
                        >
                          <View style={[
                            styles.checkbox,
                            milestone.isCompleted && styles.checkboxSelected
                          ]}>
                            {milestone.isCompleted && <Text style={styles.checkmark}>✓</Text>}
                          </View>
                          <Text style={styles.label}>Is Completed</Text>
                        </TouchableOpacity>
                      </View>
                      
                      {/* Completed Date */}
                      {milestone.isCompleted && (
                        <View style={styles.formField}>
                          <Text style={styles.label}>Completed Date</Text>
                          <TextInput
                            style={styles.input}
                            value={milestone.completedDate}
                            onChangeText={(text) => updateMilestone(milestone.id, 'completedDate', text)}
                            placeholder="e.g., Jan 2025 or 15/01/2025"
                            placeholderTextColor={theme.isDarkMode ? '#888' : '#999'}
                          />
                        </View>
                      )}
                    </>
                  )}
                </View>
              );
              })}
              
              <TouchableOpacity
                style={styles.addMilestoneButton}
                onPress={addMilestone}
              >
                <Text style={styles.addMilestoneButtonText}>+ Add Milestone</Text>
              </TouchableOpacity>
            </View>
          )}
        </View>

        {progressChevronCount > 0 && (
          <View style={styles.milestoneProgressContainer}>
            <Text style={styles.milestoneProgressTitle}>
              Milestone Progress
            </Text>
            <Text style={styles.milestoneProgressSubtitle}>
              {remainingChevronCount === 0
                ? 'All estimated milestones are complete.'
                : `${remainingChevronCount} milestone${remainingChevronCount === 1 ? '' : 's'} left to complete.`}
            </Text>
            <View style={styles.milestoneProgressRow}>
              {Array.from({ length: progressChevronCount }).map((_, index) => {
                const isComplete = isMilestoneRequiredFieldsComplete(milestones[index]);
                return (
                  <TouchableOpacity
                    key={`milestone-chevron-${index + 1}`}
                    style={[
                      styles.milestoneChevronItem,
                      index < progressChevronCount - 1 && styles.milestoneChevronItemSpacing,
                      isComplete ? styles.milestoneChevronItemComplete : styles.milestoneChevronItemPending,
                    ]}
                    onPress={() => handleMilestoneChevronPress(index)}
                    activeOpacity={0.8}
                  >
                    <Text
                      style={[
                        styles.milestoneChevronIcon,
                        isComplete ? styles.milestoneChevronComplete : styles.milestoneChevronPending,
                      ]}
                    >
                      ❯
                    </Text>
                    <Text style={styles.milestoneChevronLabel}>{index + 1}</Text>
                  </TouchableOpacity>
                );
              })}
            </View>
          </View>
        )}
        
        {/* Action Buttons */}
        <View style={styles.actionButtons}>
          <TouchableOpacity
            style={styles.refreshButton}
            onPress={handleRefresh}
          >
            <Text style={styles.refreshButtonText}>Refresh</Text>
          </TouchableOpacity>
          
          <TouchableOpacity
            style={[styles.saveButton, saving && styles.saveButtonDisabled]}
            onPress={handleSave}
            disabled={saving}
          >
            <Text style={styles.saveButtonText}>
              {saving ? 'Saving...' : 'Save'}
            </Text>
          </TouchableOpacity>
        </View>
      </ScrollView>
      
      {/* Refresh Modal */}
      <Modal
        visible={showRefreshModal}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setShowRefreshModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>Confirm Refresh</Text>
            <Text style={styles.modalText}>
              Are you sure you want to refresh and lose your unsaved progress?
            </Text>
            <View style={styles.modalButtons}>
              <TouchableOpacity
                style={styles.modalButton}
                onPress={() => setShowRefreshModal(false)}
              >
                <Text style={styles.modalButtonText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.modalButton, styles.modalButtonPrimary]}
                onPress={confirmRefresh}
              >
                <Text style={[styles.modalButtonText, styles.modalButtonTextPrimary]}>Refresh</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
      
      {/* Save Modal */}
      <Modal
        visible={showSaveModal}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setShowSaveModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>
              {milestones.length === 0 ? 'Create Goal Without Milestones?' : 'Success'}
            </Text>
            <Text style={styles.modalText}>
              {milestones.length === 0
                ? 'Are you sure you want to create a goal with no Milestones? Milestones are a great way to measure success and the platform will help you stay on top of your goals with Milestones.'
                : 'Goal and associated Milestones created'}
            </Text>
            <View style={styles.modalButtons}>
              {milestones.length === 0 ? (
                <>
                  <TouchableOpacity
                    style={styles.modalButton}
                    onPress={() => setShowSaveModal(false)}
                  >
                    <Text style={styles.modalButtonText}>Back to Milestones</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[styles.modalButton, styles.modalButtonPrimary]}
                    onPress={confirmSave}
                  >
                    <Text style={[styles.modalButtonText, styles.modalButtonTextPrimary]}>Save</Text>
                  </TouchableOpacity>
                </>
              ) : (
                <TouchableOpacity
                  style={[styles.modalButton, styles.modalButtonPrimary]}
                  onPress={confirmSave}
                >
                  <Text style={[styles.modalButtonText, styles.modalButtonTextPrimary]}>OK</Text>
                </TouchableOpacity>
              )}
            </View>
          </View>
        </View>
      </Modal>
      
      <Stopwatch ref={stopwatchRef} />
    </View>
  );
}

const getStyles = (theme) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.isDarkMode ? theme.colors.background : '#FFFFFF',
    ...(Platform.OS === 'web' ? {
      height: '100vh',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    } : {}),
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingTop: 50,
    paddingBottom: 20,
    paddingHorizontal: 20,
    backgroundColor: 'transparent',
    ...(Platform.OS === 'web' ? {
      flexShrink: 0,
      flexGrow: 0,
      position: 'relative',
      zIndex: 10,
    } : {}),
  },
  hamburgerContainer: {
    marginRight: 15,
  },
  breadcrumbContainer: {
    flex: 1,
  },
  breadcrumb: {
    fontSize: 18,
    fontWeight: '600',
    color: theme.colors.heading,
  },
  profileIconContainer: {
    backgroundColor: 'transparent',
  },
  content: {
    flex: 1,
    ...(Platform.OS === 'web' ? {
      flex: 1,
      minHeight: 0,
      overflowY: 'auto',
      overflowX: 'hidden',
      WebkitOverflowScrolling: 'touch',
      display: 'block',
      position: 'relative',
      height: 'auto',
      maxHeight: 'none',
      // Always allow scrolling
      pointerEvents: 'auto',
      touchAction: 'pan-y',
    } : {}),
  },
  contentContainer: {
    padding: 20,
    paddingBottom: 40,
  },
  title: {
    fontSize: 32,
    fontWeight: 'bold',
    color: theme.isDarkMode ? '#d4af37' : '#000000',
    marginBottom: 20,
    textAlign: 'center',
  },
  formField: {
    marginBottom: 20,
    position: 'relative',
    zIndex: 1,
  },
  formFieldDropdownOpen: {
    zIndex: 10000,
  },
  label: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.text,
    marginBottom: 8,
  },
  input: {
    backgroundColor: theme.isDarkMode ? theme.colors.surface : '#F5F5F5',
    borderRadius: 8,
    padding: 15,
    fontSize: 16,
    color: theme.colors.text,
    borderWidth: 1,
    borderColor: theme.colors.border,
  },
  helpText: {
    fontSize: 12,
    color: theme.colors.text,
    marginTop: 5,
    fontStyle: 'italic',
    opacity: 0.7,
  },
  sectionContainer: {
    marginBottom: 20,
    backgroundColor: theme.isDarkMode ? theme.colors.surface : '#F9F9F9',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: theme.colors.border,
    overflow: 'hidden',
  },
  milestoneSectionContainer: {
    width: Platform.OS === 'web' ? '60%' : '100%',
    alignSelf: 'center',
    maxWidth: Platform.OS === 'web' ? 800 : '100%',
    ...(Platform.OS === 'web' ? {
      flexShrink: 0,
      flexGrow: 0,
      minWidth: '300px',
    } : {}),
  },
  goalsDropdownTrigger: {
    borderRadius: 8,
    borderWidth: 1,
    borderColor: theme.colors.border,
    backgroundColor: theme.isDarkMode ? theme.colors.surface : '#F5F5F5',
    overflow: 'hidden',
    alignSelf: 'stretch',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 15,
  },
  goalsDropdownTriggerPressed: {
    opacity: 0.7,
  },
  sectionButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.text,
    flex: 1,
    backgroundColor: 'transparent',
  },
  sectionArrow: {
    fontSize: 12,
    color: theme.colors.text,
    backgroundColor: 'transparent',
  },
  sectionContent: {
    padding: 15,
  },
  milestoneScrollWrapper: {
    ...(Platform.OS === 'web' ? {
      height: '60vh',
      maxHeight: '60vh',
      minHeight: '200px',
      overflow: 'hidden',
      borderTopWidth: 1,
      borderTopColor: theme.colors.border,
      display: 'flex',
      flexDirection: 'column',
      // Don't block parent scroll events
      pointerEvents: 'auto',
    } : {}),
  },
  milestoneScrollView: {
    flex: 1,
    ...(Platform.OS === 'web' ? {
      height: '100%',
      overflowY: 'scroll',
      overflowX: 'hidden',
      WebkitOverflowScrolling: 'touch',
      scrollbarWidth: 'thin',
      scrollbarColor: `${theme.isDarkMode ? '#666' : '#999'} ${theme.isDarkMode ? '#333' : '#f0f0f0'}`,
      // Allow parent to scroll when not actively scrolling this view
      touchAction: 'pan-y',
    } : {
      maxHeight: 600,
    }),
  },
  milestoneScrollContent: {
    paddingBottom: 10,
  },
  tilesRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 15,
    marginBottom: 15,
  },
  coursesTile: {
    flex: 1,
    backgroundColor: theme.isDarkMode ? theme.colors.surface : '#FFFFFF',
    borderRadius: 8,
    padding: 15,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: theme.colors.border,
  },
  coursesTileImage: {
    width: '100%',
    height: 60,
    marginBottom: 10,
  },
  coursesTileButton: {
    width: '100%',
    padding: 10,
    backgroundColor: theme.colors.primary || '#d4af37',
    borderRadius: 6,
    alignItems: 'center',
  },
  coursesTileButtonText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '600',
  },
  dropdown: {
    backgroundColor: theme.isDarkMode ? theme.colors.surface : '#FFFFFF',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: theme.colors.border,
    marginTop: 10,
    maxHeight: 300,
    ...(Platform.OS === 'web' ? {
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
    } : {}),
  },
  dropdownScrollView: {
    maxHeight: 300,
    flexGrow: 0,
  },
  dropdownScrollViewWeb: {
    maxHeight: 300,
    height: 300,
    overflowY: 'auto',
    overflowX: 'hidden',
    WebkitOverflowScrolling: 'touch',
    ...(Platform.OS === 'web' ? {
      // Web-specific styles for proper scrolling
      display: 'block',
      position: 'relative',
    } : {}),
  },
  dropdownScrollContent: {
    paddingBottom: 10,
  },
  dropdownTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: theme.colors.text,
    padding: 15,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
    backgroundColor: 'transparent',
  },
  dropdownItem: {
    padding: 15,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
    backgroundColor: 'transparent',
  },
  dropdownItemText: {
    fontSize: 16,
    color: theme.colors.text,
    backgroundColor: 'transparent',
  },
  exerciseTile: {
    padding: 15,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
  },
  exerciseName: {
    fontSize: 16,
    color: theme.colors.text,
  },
  menuHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 15,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
  },
  menuHeaderText: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.text,
  },
  menuArrow: {
    fontSize: 14,
    color: theme.colors.text,
  },
  subMenu: {
    paddingLeft: 20,
    backgroundColor: theme.isDarkMode ? '#1a1a1a' : '#F9F9F9',
  },
  goalsTableContainer: {
    marginTop: 10,
    borderWidth: 1,
    borderColor: theme.colors.border,
    borderRadius: 8,
    backgroundColor: theme.isDarkMode ? '#121212' : '#FFFFFF',
    overflow: 'hidden',
  },
  goalsTableHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: theme.isDarkMode ? '#1a1a1a' : '#F1F1F1',
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
    paddingVertical: 10,
    paddingHorizontal: 12,
  },
  goalsTableHeaderText: {
    fontSize: 12,
    fontWeight: '700',
    color: theme.colors.text,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  goalsTableGoalCol: {
    flex: 2.2,
    marginRight: 8,
  },
  goalsTableExerciseCol: {
    flex: 1.8,
    marginRight: 8,
  },
  goalsTableSelectCol: {
    width: 82,
    textAlign: 'center',
  },
  goalsTableScroll: {
    maxHeight: 300,
  },
  goalsTableScrollContent: {
    paddingBottom: 6,
  },
  goalsTableRowBlock: {
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
  },
  goalsTableRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    paddingHorizontal: 12,
    backgroundColor: theme.isDarkMode ? '#141414' : '#FFFFFF',
  },
  goalsTableRowExpanded: {
    backgroundColor: theme.isDarkMode ? '#1b1b1b' : '#F8F8F8',
  },
  goalsTableCellText: {
    fontSize: 14,
    color: theme.colors.text,
  },
  goalsSelectButton: {
    width: 82,
    backgroundColor: theme.colors.primary || '#d4af37',
    borderRadius: 6,
    paddingVertical: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  goalsSelectButtonText: {
    color: '#FFFFFF',
    fontSize: 13,
    fontWeight: '700',
  },
  goalsTableRowDetails: {
    paddingHorizontal: 12,
    paddingVertical: 10,
    backgroundColor: theme.isDarkMode ? '#0f0f0f' : '#FAFAFA',
    borderTopWidth: 1,
    borderTopColor: theme.colors.border,
  },
  goalsDetailsTitle: {
    fontSize: 14,
    fontWeight: '700',
    color: theme.colors.text,
    marginBottom: 6,
  },
  goalsDetailsText: {
    fontSize: 13,
    color: theme.colors.text,
    marginBottom: 4,
    opacity: 0.9,
  },
  goalsTableEmptyText: {
    padding: 16,
    textAlign: 'center',
    color: theme.colors.text,
    opacity: 0.8,
  },
  createExerciseButton: {
    backgroundColor: '#d4af37',
    padding: 15,
    borderRadius: 8,
    alignItems: 'center',
    marginBottom: 20,
  },
  createExerciseButtonText: {
    color: theme.isDarkMode ? '#FFFFFF' : '#000000',
    fontSize: 16,
    fontWeight: '600',
  },
  dropdownButtonText: {
    fontSize: 16,
    color: theme.colors.text,
    flex: 1,
    backgroundColor: 'transparent',
  },
  dropdownArrow: {
    fontSize: 12,
    color: theme.colors.text,
    backgroundColor: 'transparent',
  },
  dropdownOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'transparent',
    zIndex: 9999,
  },
  dropdownMenu: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    backgroundColor: theme.isDarkMode ? theme.colors.surface : '#FFFFFF',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: theme.colors.border,
    marginTop: 5,
    maxHeight: 200,
    zIndex: 10001,
  },
  dropdownScrollView: {
    maxHeight: 200,
  },
  dropdownOption: {
    padding: 15,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
    backgroundColor: 'transparent',
  },
  dropdownOptionText: {
    fontSize: 16,
    color: theme.colors.text,
    backgroundColor: 'transparent',
  },
  checkboxContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  checkbox: {
    width: 24,
    height: 24,
    borderRadius: 4,
    borderWidth: 2,
    borderColor: theme.colors.primary || '#d4af37',
    marginRight: 10,
    justifyContent: 'center',
    alignItems: 'center',
  },
  checkboxSelected: {
    backgroundColor: theme.colors.primary || '#d4af37',
  },
  checkmark: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: 'bold',
    backgroundColor: 'transparent',
  },
  milestoneForm: {
    backgroundColor: theme.isDarkMode ? '#1a1a1a' : '#FFFFFF',
    borderRadius: 8,
    padding: 15,
    marginBottom: 15,
    borderWidth: 1,
    borderColor: theme.colors.border,
  },
  milestoneHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
    width: '100%',
    paddingVertical: 8,
  },
  milestoneHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    paddingRight: 10,
  },
  milestoneExpandIcon: {
    fontSize: 14,
    color: theme.colors.text,
    marginRight: 8,
  },
  milestoneNumber: {
    fontSize: 18,
    fontWeight: 'bold',
    color: theme.colors.text,
  },
  deleteButton: {
    padding: 5,
  },
  deleteButtonText: {
    fontSize: 20,
  },
  addMilestoneButton: {
    backgroundColor: theme.colors.primary || '#d4af37',
    padding: 15,
    borderRadius: 8,
    alignItems: 'center',
    marginTop: 10,
  },
  addMilestoneButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
  milestoneProgressContainer: {
    marginTop: 15,
    marginBottom: 10,
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: theme.colors.border,
    backgroundColor: theme.isDarkMode ? theme.colors.surface : '#F9F9F9',
  },
  milestoneProgressTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: theme.colors.text,
    marginBottom: 4,
  },
  milestoneProgressSubtitle: {
    fontSize: 13,
    color: theme.colors.text,
    opacity: 0.8,
    marginBottom: 10,
  },
  milestoneProgressRow: {
    flexDirection: 'row',
    alignItems: 'stretch',
    width: '100%',
    justifyContent: 'space-between',
    paddingVertical: Platform.OS === 'web' ? 10 : 8,
  },
  milestoneChevronItem: {
    flex: 1,
    minWidth: 0,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: Platform.OS === 'web' ? 88 : 76,
    borderRadius: 12,
    borderWidth: 1.5,
    borderColor: theme.colors.border,
    paddingHorizontal: Platform.OS === 'web' ? 10 : 8,
    paddingVertical: Platform.OS === 'web' ? 10 : 8,
  },
  milestoneChevronItemSpacing: {
    marginRight: Platform.OS === 'web' ? 10 : 8,
  },
  milestoneChevronItemPending: {
    backgroundColor: theme.isDarkMode ? 'rgba(212, 175, 55, 0.2)' : 'rgba(212, 175, 55, 0.18)',
  },
  milestoneChevronItemComplete: {
    backgroundColor: theme.isDarkMode ? 'rgba(46, 158, 72, 0.24)' : 'rgba(46, 158, 72, 0.18)',
  },
  milestoneChevronIcon: {
    fontSize: Platform.OS === 'web' ? 58 : 50,
    lineHeight: Platform.OS === 'web' ? 58 : 50,
    fontWeight: '700',
    marginLeft: Platform.OS === 'web' ? 4 : 2,
  },
  milestoneChevronPending: {
    color: '#d4af37',
  },
  milestoneChevronComplete: {
    color: '#2e9e48',
  },
  milestoneChevronLabel: {
    marginTop: 6,
    fontSize: Platform.OS === 'web' ? 15 : 13,
    fontWeight: '600',
    color: theme.isDarkMode ? '#FFFFFF' : '#111111',
    opacity: 1,
  },
  actionButtons: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 30,
    gap: 15,
  },
  refreshButton: {
    flex: 1,
    backgroundColor: '#999',
    padding: 18,
    borderRadius: 8,
    alignItems: 'center',
  },
  refreshButtonText: {
    color: '#FFFFFF',
    fontSize: 18,
    fontWeight: '600',
  },
  saveButton: {
    flex: 1,
    backgroundColor: theme.colors.primary || '#d4af37',
    padding: 18,
    borderRadius: 8,
    alignItems: 'center',
  },
  saveButtonDisabled: {
    opacity: 0.5,
  },
  saveButtonText: {
    color: '#FFFFFF',
    fontSize: 18,
    fontWeight: '600',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    backgroundColor: theme.isDarkMode ? theme.colors.surface : '#FFFFFF',
    borderRadius: 12,
    padding: 25,
    width: width * 0.85,
    maxWidth: 400,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: theme.colors.text,
    marginBottom: 15,
    textAlign: 'center',
  },
  modalText: {
    fontSize: 16,
    color: theme.colors.text,
    marginBottom: 25,
    textAlign: 'center',
    lineHeight: 22,
  },
  modalButtons: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 15,
  },
  modalButton: {
    flex: 1,
    padding: 15,
    borderRadius: 8,
    alignItems: 'center',
    backgroundColor: theme.isDarkMode ? '#333' : '#F5F5F5',
  },
  modalButtonPrimary: {
    backgroundColor: '#d4af37',
  },
  modalButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.text,
  },
  modalButtonTextPrimary: {
    color: '#FFFFFF',
  },
});
